import { Button, Collapse, Group, IconButton, iconSizes, Paper, Stack, Text } from '@common/ui'
import { IconChevronDown, IconTrash } from '@common/ui/icons'
import TrimmedTextInput from './TrimmedTextInput'
import { com } from '@eidu/web'
import ItemList from './ItemList'
import { withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'
import NextElementDisplay from '../data/NextElementDisplay'
import NextElementInput from './NextElementInput'
import { LocalNext, localNextFromNext, nextFromLocalNext } from '../../domain/form/definition/LocalNext'
import { useOtherPages } from './OtherPagesProvider'
import Page = com.eidu.sharedlib.form.definition.Page

type FormPageProps = {
  page: Page
  onInsertAfter: () => void
  onDelete?: () => void
  onChange?: (currentKey: string, newPage: Page) => void
  isExpanded: boolean
  onExpand: () => void
}

const FormPage = ({ page, onInsertAfter, onDelete, onChange, isExpanded, onExpand }: FormPageProps) => {
  const pageKey = page.key ?? ''

  const otherPages = useOtherPages()

  return (
    <Stack gap="xs">
      <Paper shadow="xs" p="md" radius="xs" data-testid="form-page-card">
        <Stack gap="md">
          <Group gap="xs" align="center" wrap="nowrap">
            <IconButton
              size="sm"
              onClick={onExpand}
              aria-expanded={isExpanded}
              aria-label="toggle page expansion"
              style={{
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 200ms',
              }}
            >
              <IconChevronDown size={iconSizes.sm} stroke={1.5} />
            </IconButton>
            {onChange ? (
              <TrimmedTextInput
                value={page.title}
                onChange={(e) => onChange(pageKey, withKey(page.copy(e.target.value), page))}
                style={{ flex: 1 }}
              />
            ) : (
              <Text fw={500} style={{ flex: 1 }}>
                {page.title}
              </Text>
            )}
            {onDelete && (
              <IconButton aria-label="delete page" onClick={onDelete} size="sm">
                <IconTrash size={iconSizes.sm} stroke={1.5} />
              </IconButton>
            )}
          </Group>

          {/* flex-start: the key field is taller (helper text), so the Go to select aligns with its top edge */}
          <Group gap="md" align="flex-start" wrap="nowrap">
            {onChange && (
              <FormKeyField
                value={pageKey}
                onChange={(e) => onChange(pageKey, withKey(page.copy(page.title, page.items, e.target.value), page))}
                disabled={false}
              />
            )}
            {!onChange ? (
              <NextElementDisplay
                value={page.nextPage ? localNextFromNext(page.nextPage) : undefined}
                options={otherPages}
                type={'page'}
              />
            ) : (
              <NextElementInput
                value={page.nextPage ? localNextFromNext(page.nextPage) : undefined}
                parentKey={pageKey}
                type={'page'}
                options={otherPages}
                onChange={(newValue: LocalNext | undefined) =>
                  onChange(
                    pageKey,
                    withKey(
                      page.copy(page.title, page.items, page.key, newValue ? nextFromLocalNext(newValue) : null),
                      page
                    )
                  )
                }
              />
            )}
          </Group>

          <Collapse expanded={isExpanded} transitionDuration={200}>
            <ItemList
              items={page.items}
              onChange={onChange && ((newItems) => onChange(pageKey, withKey(page.copy(page.title, newItems), page)))}
            />
          </Collapse>
        </Stack>
      </Paper>
      {onChange && (
        <Button variant="outline" size="sm" onClick={onInsertAfter} style={{ alignSelf: 'center' }}>
          Insert Page
        </Button>
      )}
    </Stack>
  )
}

export default FormPage
